import * as React from "react";
import { DatePicker } from "../../../../../../../react-core/Datepicker/DatePicker";
import { ActionType } from "../../../../../../../react-core/Flux/ActionType";
import { MoneyTextBoxEditor } from "../../../../../../../react-core/Html/Money/MoneyTextBoxEditor";
import { NumberTextBoxEditor } from "../../../../../../../react-core/Html/NumberTextBoxEditor";
import { JsonDateParser } from "../../../../../../../react-core/Utils/JsonDateParser";
import { CurrencyCode } from "../../../../../../../Wrap/Constants/Enums/Domain/CurrencyCode";
import { SubAccountType } from "../../../../../../../Wrap/Constants/Enums/Domain/SubAccountType";
import { ServiceProposition } from "../../../../../../../Wrap/Constants/Enums/ServiceProposition";
import { PropositionInvestmentConfig } from "../../../../../../../Wrap/Models/Domains/Account/PropositionInvestmentConfig";
import { TransferDestinationAccountModel } from "../../../../../../../Wrap/Models/Domains/Transfer/TransferDestinationAccountModel";
import { TransferISTSelection } from "../../../../../../../Wrap/Models/Models/InstrumentSelection/TransferISTSelection";
import { IsaTransfer } from "../../../../../../../Wrap/Models/Models/Processes/OneOffPayment/Isa/IsaTransfer";
import { CompanyWizardAction } from "../../../../../../Admin/Company/CompanyWizard/Actions/CompanyWizardAction";
import { InvestmentSelectionAction } from "../../../../../../Shared/IST/Action/InvestmentSelectionAction";
import { SearchTabs } from "../../../../../../Shared/IST/Components/Shared/SearchTabs";
import { ReRegTransferStore, SelectedInvestmentStore } from "../../../../../../Shared/IST/Store/Variable";
import { IProcessComponentProps, ProcessComponent } from "../../../../../../Shared/Process/Process/ProcessComponent";
import { Table } from "../../../../../../Shared/Table/Table";
import { getQueryParameters } from "../../../../../../Shared/Wizards/Entities/QueryParametersHelper";
import { AdviserContextStoreStatic } from "../../../NewBusinessWizard/Stores/Variable";
import { SubAccountTypeHelper } from "../../Helpers/SubAccountTypeHelper";
import { datePickerOptions } from "../../Resource/Contents";
import { InvestmentType } from "../../../../../../../Wrap/Constants/Enums/InvestmentType";
import { InvestorType } from "../../../../../../../Wrap/Models/Domains/Account/SlAccounts/InvestorType";
import { ClientAccountStore } from "../../../../../Stroes/Variable";

interface IReRegInvestmentSeletionProps extends IProcessComponentProps {
    data: TransferISTSelection[];
    type: SubAccountType;
    modelData?: IsaTransfer;
    onReregValueChange?: (v) => void;
    isInternal: boolean;
    index: number;
    accountServiceProposition: ServiceProposition;
    transferDestinationAccount?: TransferDestinationAccountModel;
    isApsIsa?: boolean;
    subAccountInstructionHierarchy?: string;
}

interface IReRegInvestmentSeletionState {
    investmenPermission: PropositionInvestmentConfig[];
    companyServicePropostion: ServiceProposition[];
}

export class ReRegInvestmentSeletion extends ProcessComponent<
    IReRegInvestmentSeletionProps,
    IReRegInvestmentSeletionState
> {
    constructor(props: any) {
        super(props);
        this.state = {
            investmenPermission: [],
            companyServicePropostion: [],
        };

        this.subscribeToStore(SelectedInvestmentStore, () => this.onStoreChange());
    }

    private onStoreChange() {
        if (this.props.isInternal) {return;}
        const { data, index } = this.props;
        if (SelectedInvestmentStore.getActionType() == ActionType.Success) {
            if (ReRegTransferStore.getData() !== index) {return;}
            const newItem = SelectedInvestmentStore.getData();
            const _data = newItem.filter((item) =>
                data.every((ele) => ele.Investment.ProductId !== item.Investment.ProductId)
            );
            this.props.onChange(data.concat(_data));
        }
    }

    componentDidMount() {
        super.componentDidMount();
        if (!this.props.data) {this.props.onChange([]);}

        if (!this.props.isInternal) {
            const clientAccount = ClientAccountStore.getClientAccountInfo();
            if (clientAccount?.AccountServicePropositionId) {
                InvestmentSelectionAction.getInvestmentPermission(this.props.accountServiceProposition, (res) => {
                    const newRes = [...res];
                    const elementIndex = newRes.findIndex((x) => x.InvestmentTypeId == InvestmentType.CashDeposits);
                    if (elementIndex !== -1) {
                        newRes[elementIndex].Enable = (clientAccount.InvestorType == InvestorType.Individ || 
                            clientAccount.InvestorType == InvestorType.Individual ||
                            clientAccount.InvestorType == InvestorType.Joint ||
                            clientAccount.InvestorType == InvestorType.JointIndividual)
                    }
                    this.setState({ investmenPermission: newRes });
                });
            }
        }
        const adv = AdviserContextStoreStatic.getData();
        if (adv?.Company) {
            CompanyWizardAction.getViewCompany(adv?.Company, (res) => {
                this.setState({ companyServicePropostion: res?.ServicePropositions });
            });
        }
    }

    private onInvestmentChange(changes, index) {
        const data = this.props.data.concat();
        if (changes == -1) {
            data.splice(index, 1);
        } else {
            for (const key in changes) {
                data[index]["Investment"][key] = changes[key];
            }
        }
        return this.props.onChange(data);
    }

    private onUnitChange(v, index) {
        const data = this.props.data.concat();
        data[index]["Units"] = v;
        data[index]["QuotationValue"] = {
            Amount: v * (data[index]?.Investment?.UnitPrice?.Amount || 0),
            Currency: CurrencyCode.GBP,
        };
        return this.props.onChange(data);
    }

    private getTableCols() {
        const cols = [
            {
                title: "Investment code",
                key: "Investment.InstrumentCode",
            },
            {
                title: "Investment description",
                key: "Investment.DisplayName",
            },
            {
                title: "Quantity (units)",
                key: "Units",
                thCssWrapper: "cell-20",
                tdCssWrapper: "cell-right",
                render: (data, investment: TransferISTSelection, index) => {
                    return (
                        <NumberTextBoxEditor
                            cssWrapper="control-size-80 mb-0"
                            atLeast={{ value: 0, errorMessage: "Quantity entered must be ≥ 0" }}
                            value={data}
                            onChange={(v) => this.onUnitChange(v, index)}
                            maxNumberOfDecimalPlaces={{
                                errorMessage: "only allow four decimal places",
                                value: 4,
                            }}
                            required="Please enter"
                        />
                    );
                },
            },
            {
                title: "Price (£)",
                key: "Investment.UnitPrice.Amount",
                thCssWrapper: "cell-right",
                tdCssWrapper: "cell-right",
                render: (data, investment: TransferISTSelection, index) => {
                    return <span>{data ? Number(data).toFixed(4) : 0}</span>;
                },
            },
            {
                title: "Value (£)",
                key: "Investment.UnitPrice.Amount",
                thCssWrapper: "cell-right",
                tdCssWrapper: "cell-right",
                render: (data, investment: TransferISTSelection, index) => {
                    return <span>{Number(((data || 0) * (investment.Units || 0)).toFixed(2))}</span>;
                },
            },
            SubAccountTypeHelper.isPP(this.props.type) && !this.IsSameHeaderTransfer()
                ? {
                      title: "Tax book cost (£)",
                      key: "Investment.TaxBookCost.Amount",
                      thCssWrapper: "cell-right cell-20",
                      tdCssWrapper: "cell-right",
                      render: (data, investment: TransferISTSelection, index) => {
                          return (
                              <NumberTextBoxEditor
                                  cssWrapper="control-size-80 mb-0"
                                  value={data ? Number(data.toFixed(4)) : null}
                                  onChange={(v) =>
                                      this.onInvestmentChange(
                                          { TaxBookCost: { Amount: v, Currency: CurrencyCode.GBP } },
                                          index
                                      )
                                  }
                                  maxNumberOfDecimalPlaces={{
                                      errorMessage: "only allow four decimal places",
                                      value: 4,
                                  }}
                              />
                          );
                      },
                  }
                : null,
            SubAccountTypeHelper.isPP(this.props.type) && !this.IsSameHeaderTransfer()
                ? {
                      title: "Unit Tax Book Cost (£)",
                      key: "UnitTaxBookCost",
                      thCssWrapper: "cell-right",
                      tdCssWrapper: "cell-right",
                      render: (data, investment: TransferISTSelection, index) => {
                          return (
                              <span>
                                  {Number(
                                      ((investment.Units || 0) === 0
                                          ? 0
                                          : (investment.Investment.TaxBookCost
                                                ? investment.Investment.TaxBookCost.Amount
                                                : 0) / investment.Units
                                      ).toFixed(4)
                                  )}
                              </span>
                          );
                      },
                  }
                : null,
            SubAccountTypeHelper.isPP(this.props.type) && !this.IsSameHeaderTransfer()
                ? {
                      title: "Acquisition Date",
                      thCssWrapper: "cell-34",
                      key: "Investment.AcquisitionDate",
                      render: (data, investment: TransferISTSelection, index) => {
                          return (
                              <DatePicker
                                  formGroupCssWrapper="control-size-180 mb-0"
                                  selected={JsonDateParser.toDateNoOffset(data)}
                                  onChange={(v) =>
                                      this.onInvestmentChange(
                                          { AcquisitionDate: JsonDateParser.toJsonDateNoOffset(v) },
                                          index
                                      )
                                  }
                                  {...datePickerOptions}
                              />
                          );
                      },
                  }
                : null,
            !this.props.isInternal
                ? {
                      title: "Remove",
                      key: "Remove",
                      tdCssWrapper: "cell-center",
                      render: (data, investment: TransferISTSelection, index) => {
                          return (
                              <button
                                  className="remove-button label-sr-only"
                                  title="Delete"
                                  onClick={() => this.onInvestmentChange(-1, index)}
                              >
                                  <span className="icon" aria-hidden="true"></span>
                                  <span className="text">remove portfolio</span>
                              </button>
                          );
                      },
                  }
                : null,
        ];
        return cols;
    }

    private IsSameHeaderTransfer(): boolean {
        const clAccountId = getQueryParameters()["claccountid"];
        const accountSelected = this.props.transferDestinationAccount;
        if (!accountSelected) {return false;}
        if (clAccountId?.split("-")?.[0] == accountSelected?.ClAccountId?.split("-")?.[0]) {
            return true;
        }
        return false;
    }

    private getTotal() {
        let total = this.props.data.reduce(
            (sum, next) => sum + (next.Investment?.UnitPrice?.Amount || 0) * next.Units,
            0
        );
        if (this.props.type == SubAccountType.StocksIsa) {
            total = total + (this.props.modelData?.ReRegisterValue?.Amount || 0);
        }
        return total;
    }

    public render(): JSX.Element {
        if (!this.props.data) {return null;}

        const params = getQueryParameters();
        return (
            <div className="ist-wrap">
                {(this.props.type == SubAccountType.InvestmentAccount ||
                    (this.props.type == SubAccountType.StocksIsa &&
                        this.props.modelData?.InvestmentsToBeTransferredInSpecie == true) || this.props.type == SubAccountType.Npr || this.props.type == SubAccountType.JuniorSipp) && (
                    <>
                        <h3 className="mb-24">
                            {this.props.isInternal
                                ? "Existing investments to be transferred"
                                : "Assets to be re-registered"}
                        </h3>
                        {!this.props.isInternal && (
                            <SearchTabs
                                isUnderTransfer={true}
                                hiercharyId={params["subaccounthierarchyid"] ?? this.props.subAccountInstructionHierarchy}
                                product={this.props.type}
                                data={this.props.data}
                                index={this.props.index}
                                investmentPermission={this.state.investmenPermission}
                                accountServiceProposition={this.props.accountServiceProposition}
                                canBeBought={false}
                                canBeSold={false}
                                companyServiceProposition={this.state.companyServicePropostion}
                            />
                        )}

                        <Table
                            cssWrapper="no-sided-padding is-allocation-table"
                            data={this.props.data}
                            col={this.getTableCols()}
                            showEmptyMsg
                        />
                        <p className="warning-message">
                            If more units are held than specified, we will transfer all units held for this account
                            number.
                        </p>
                    </>
                )}
                {this.props.type == SubAccountType.StocksIsa && !this.props.isApsIsa && (
                    <MoneyTextBoxEditor
                        cssWrapper="control-size-245"
                        value={this.props.modelData?.ReRegisterValue?.Amount}
                        currency={CurrencyCode.GBP}
                        label={"Estimated cash amount being transferred"}
                        onChange={(v) => this.props.onReregValueChange(v)}
                        maxNumberOfDecimalPlaces={{
                            errorMessage: "only allow four decimal places",
                            value: 4,
                        }}
                        required={
                            this.props.modelData?.InvestmentsToBeTransferredInSpecie == false ? "Please enter" : null
                        }
                        moreThan={
                            this.props.modelData?.InvestmentsToBeTransferredInSpecie == false
                                ? {
                                      value: 0,
                                      errorMessage: "Value must larger than 0",
                                  }
                                : null
                        }
                        infoText={
                            "Use this field to capture residual cash of funds that are unable to be moved in-specie"
                        }
                    />
                )}
                {this.props.data.length > 0 && (
                    <MoneyTextBoxEditor
                        cssWrapper="control-size-245 pb-25"
                        value={Number(this.getTotal().toFixed(2))}
                        currency={CurrencyCode.GBP}
                        label={"Total estimated transfer"}
                        disabled={true}
                        allowDecimals={true}
                    />
                )}
            </div>
        );
    }
}
